import { Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';
import { ExpansionPanel } from '../../../shared/components/expansion-panel/expansion-panel.component';
import { LabelRequiredDirective } from '../../../shared/directives/label-required.directive';
import { Phase } from '../../référentiel-métier-processus/processus-phases/models/phase.model';
import { Interrelation } from '../models/interrelation.model';

@Component({
  selector: 'app-interrelation-group',
  imports: [
    HlmSelectImports,
    BrnSelectImports,
    HlmInputImports,
    HlmFieldImports,
    ExpansionPanel,
    ReactiveFormsModule,
    HlmAutocompleteImports,
    LabelRequiredDirective,
  ],
  providers: [
    provideHlmAutocompleteConfig({
      transformValueToSearch: (option: Interrelation) => `${option.nom}`,
    }),
  ],
  templateUrl: './interrelation-group.component.html',
})
export class InterrelationGroupComponent implements OnInit {
  readonly #destroy = inject(DestroyRef);

  form = input.required<FormGroup>();
  readonly index = input.required<number>();
  readonly referentiel = input.required<Référentiel>();
  readonly phases = input.required<Phase[]>();
  readonly interrelations = input.required<Interrelation[]>();
  public readonly interrelationsFiltrees = computed(() =>
    this.interrelations().filter(
      (interrelation) =>
        interrelation.nom.toLowerCase().includes(this.search().toLowerCase()) ||
        `${interrelation.nom}`.toLowerCase().includes(this.search().toLowerCase()),
    ),
  );
  search = signal('');
  nouvellesInterrelations = signal<Omit<Interrelation, 'id'>[]>([]);
  toutesLesInterrelations = computed(() => [...this.interrelationsFiltrees(), ...this.nouvellesInterrelations()]);

  ngOnInit(): void {
    this.interrelation.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.#destroy), distinctUntilChanged())
      .subscribe(() => {
        if (this.interrelation.value.idsPhase) {
          this.phasesForm.setValue(this.phases().filter((x) => this.interrelation.value.idsPhase?.includes(x.id)));
        }
      });
  }

  onSearchChange(texte: string) {
    const nom = texte.trim();
    if (!nom || this.interrelation.value?.nom === nom) {
      return;
    }

    const existant = this.interrelations().find((x) => x.nom.toLowerCase() === nom.toLowerCase());
    this.interrelation.setValue(existant ?? { nom, idsPhase: [] }, { emitModelToViewChange: false });
  }

  isFormInvalid() {
    return (this.interrelation.value?.nom === '' || this.search() === '') && this.form().touched;
  }

  get interrelation() {
    return this.form().get('interrelation') as FormControl<{ nom: string; idsPhase?: string[] }>;
  }

  get phasesForm() {
    return this.form().get('phases') as unknown as FormControl<Phase[]>;
  }
}
